/**
 * Topology-data builders for InfrastructureMapCanvas.
 *
 * - buildBusTopologyData: groups confirmed-link components into bus groups
 *   with a hub, visible nodes, and a count of collapsed members.
 * - buildTreeTopologyData: infers parent/child structure from confirmed
 *   relationships and y-coordinates, then projects it onto the rendered
 *   nodes.
 */

import type { CanvasPosition, EndpointDisplayInfo, MapStyle } from '../../types/infrastructure-map'
import type { EndpointRelationship } from '../../types/case-container'
import type {
  BusTopologyGroup,
  BusTopologyNode,
  RenderableNode,
  TreeTopologyGroup,
  TreeTopologyNode,
} from './infrastructure-map-canvas.helpers'
import {
  buildConfirmedAdjacency,
  ensureNeighborSet,
  findConnectedComponents,
} from './infrastructure-map-canvas.helpers'

export interface BusTopologyInput {
  mapStyle: MapStyle
  displayInfo: Map<string, EndpointDisplayInfo>
  relationships: EndpointRelationship[]
  busCompressedPositions: Map<string, CanvasPosition>
}

export function buildBusTopologyData(input: BusTopologyInput): BusTopologyGroup[] {
  const { mapStyle, displayInfo, relationships, busCompressedPositions } = input
  if (mapStyle !== 'bus') return []
  const adjacency = buildConfirmedAdjacency(relationships)
  const components = findConnectedComponents(adjacency)
  const result: BusTopologyGroup[] = []
  components.forEach((component, componentIndex) => {
    const group = buildBusGroup(component, componentIndex, {
      displayInfo,
      adjacency,
      busCompressedPositions,
    })
    if (group) result.push(group)
  })
  return result
}

interface BusGroupNodeInfo {
  endpointId: string
  isCollapsed: boolean
  connections: number
}

interface BusGroupCtx {
  displayInfo: Map<string, EndpointDisplayInfo>
  adjacency: Map<string, Set<string>>
  busCompressedPositions: Map<string, CanvasPosition>
}

function gatherBusGroupNodes(
  component: string[],
  displayInfo: Map<string, EndpointDisplayInfo>,
  adjacency: Map<string, Set<string>>
): { allNodesInfo: BusGroupNodeInfo[]; hubIndex: number } {
  let hubIndex = 0
  let maxConnections = 0
  const allNodesInfo: BusGroupNodeInfo[] = []
  for (const [i, endpointId] of component.entries()) {
    const info = displayInfo.get(endpointId)
    const connections = adjacency.get(endpointId)?.size ?? 0
    if (connections > maxConnections) {
      maxConnections = connections
      hubIndex = i
    }
    allNodesInfo.push({ endpointId, isCollapsed: info?.isCollapsed ?? false, connections })
  }
  return { allNodesInfo, hubIndex }
}

function buildBusGroup(
  component: string[],
  lanGroupId: number,
  ctx: BusGroupCtx
): BusTopologyGroup | null {
  const { displayInfo, adjacency, busCompressedPositions } = ctx
  const { allNodesInfo, hubIndex } = gatherBusGroupNodes(component, displayInfo, adjacency)
  if (allNodesInfo.length === 0) return null
  const hubInfo = allNodesInfo[hubIndex]
  if (!hubInfo) return null
  const hubPosition = busCompressedPositions.get(hubInfo.endpointId)
  if (!hubPosition) return null

  const collapsedEndpointIds: string[] = []
  const visibleNodes: BusTopologyNode[] = []
  collectBusGroupVisibleNodes({
    allNodesInfo,
    hubIndex,
    busCompressedPositions,
    visibleNodes,
    collapsedEndpointIds,
  })

  return {
    lanGroupId,
    busY: hubPosition.y,
    hubId: hubInfo.endpointId,
    collapsedCount: collapsedEndpointIds.length,
    collapsedEndpointIds,
    nodes: visibleNodes,
  }
}

interface CollectBusVisibleArgs {
  allNodesInfo: BusGroupNodeInfo[]
  hubIndex: number
  busCompressedPositions: Map<string, CanvasPosition>
  visibleNodes: BusTopologyNode[]
  collapsedEndpointIds: string[]
}

function collectBusGroupVisibleNodes(args: CollectBusVisibleArgs): void {
  const { allNodesInfo, hubIndex, busCompressedPositions, visibleNodes, collapsedEndpointIds } =
    args
  for (const [i, nodeInfo] of allNodesInfo.entries()) {
    const isHub = i === hubIndex
    const position = busCompressedPositions.get(nodeInfo.endpointId)
    if (isHub && position) {
      visibleNodes.push({
        endpointId: nodeInfo.endpointId,
        position,
        isHub: true,
        childCount: 0,
        isCollapsed: false,
      })
    } else if (nodeInfo.isCollapsed) {
      collapsedEndpointIds.push(nodeInfo.endpointId)
    } else if (position) {
      visibleNodes.push({
        endpointId: nodeInfo.endpointId,
        position,
        isHub: false,
        childCount: 1,
        isCollapsed: false,
      })
    }
  }
}

export interface TreeTopologyInput {
  mapStyle: MapStyle
  displayInfo: Map<string, EndpointDisplayInfo>
  nodesToRender: RenderableNode[]
  relationships: EndpointRelationship[]
}

export function buildTreeTopologyData(input: TreeTopologyInput): TreeTopologyGroup[] {
  const { mapStyle, displayInfo, nodesToRender, relationships } = input
  if (mapStyle !== 'tree') return []
  const adjacency = buildStructuralAdjacency(relationships)
  const { parentOf, childrenByParent } = inferTreeStructure(displayInfo, adjacency)
  const currentPositionById = new Map<string, CanvasPosition>()
  for (const node of nodesToRender) currentPositionById.set(node.endpointId, node.position)
  const allNodes = buildTreeAllNodes({
    nodesToRender,
    parentOf,
    childrenByParent,
    displayInfo,
    currentPositionById,
  })
  return allNodes.length > 0 ? [{ lanGroupId: 0, nodes: allNodes }] : []
}

function buildStructuralAdjacency(relationships: EndpointRelationship[]): Map<string, Set<string>> {
  const adjacency = new Map<string, Set<string>>()
  for (const rel of relationships) {
    if (rel.status === 'open-question' || rel.status === 'suspected') continue
    ensureNeighborSet(adjacency, rel.sourceEndpointId).add(rel.targetEndpointId)
    ensureNeighborSet(adjacency, rel.targetEndpointId).add(rel.sourceEndpointId)
  }
  return adjacency
}

function inferTreeStructure(
  displayInfo: Map<string, EndpointDisplayInfo>,
  adjacency: Map<string, Set<string>>
): { parentOf: Map<string, string>; childrenByParent: Map<string, string[]> } {
  const originalPositionById = new Map<string, CanvasPosition>()
  for (const [endpointId, info] of displayInfo) {
    if (info.position) originalPositionById.set(endpointId, info.position)
  }
  const parentOf = new Map<string, string>()
  const childrenByParent = new Map<string, string[]>()
  for (const [endpointId, info] of displayInfo) {
    if (!info.position) continue
    const parentId = chooseClosestUpperNeighbor({
      endpointId,
      nodeY: info.position.y,
      adjacency,
      originalPositionById,
    })
    if (parentId) {
      parentOf.set(endpointId, parentId)
      const siblings = childrenByParent.get(parentId) ?? []
      siblings.push(endpointId)
      childrenByParent.set(parentId, siblings)
    }
  }
  return { parentOf, childrenByParent }
}

interface ClosestUpperNeighborArgs {
  endpointId: string
  nodeY: number
  adjacency: Map<string, Set<string>>
  originalPositionById: Map<string, CanvasPosition>
}

function chooseClosestUpperNeighbor(args: ClosestUpperNeighborArgs): string | null {
  const { endpointId, nodeY, adjacency, originalPositionById } = args
  const neighbors = adjacency.get(endpointId)
  if (!neighbors) return null
  let parentId: string | null = null
  let bestParentY = nodeY
  for (const neighborId of neighbors) {
    const neighborPos = originalPositionById.get(neighborId)
    if (!neighborPos || neighborPos.y >= bestParentY) continue
    if (parentId === null) {
      parentId = neighborId
      bestParentY = neighborPos.y
      continue
    }
    const currentBestY = originalPositionById.get(parentId)?.y ?? -Infinity
    if (neighborPos.y > currentBestY) {
      parentId = neighborId
      bestParentY = neighborPos.y
    }
  }
  return parentId
}

interface BuildTreeAllNodesArgs {
  nodesToRender: RenderableNode[]
  parentOf: Map<string, string>
  childrenByParent: Map<string, string[]>
  displayInfo: Map<string, EndpointDisplayInfo>
  currentPositionById: Map<string, CanvasPosition>
}

function buildTreeAllNodes(args: BuildTreeAllNodesArgs): TreeTopologyNode[] {
  const { nodesToRender, parentOf, childrenByParent, displayInfo, currentPositionById } = args
  const result: TreeTopologyNode[] = []
  for (const node of nodesToRender) {
    const parent = parentOf.get(node.endpointId)
    const children = childrenByParent.get(node.endpointId) ?? []
    const effectiveParent = parent && currentPositionById.has(parent) ? parent : null
    const effectiveChildren = children.filter((childId) => currentPositionById.has(childId))
    result.push({
      endpointId: node.endpointId,
      position: node.position,
      parentId: effectiveParent,
      hasChildren: effectiveChildren.length > 0,
      childCount: effectiveChildren.length,
      isCollapsed: displayInfo.get(node.endpointId)?.isCollapsed ?? false,
    })
  }
  return result
}
